/**
 * Amend Hearing Details — §14b's form, and the second modal on this page.
 *
 * `role="dialog" aria-modal="true"`, closing on Cancel, Close, backdrop click or Escape, in
 * `CreateHearingModal`'s own shell: this is the same act on the same record from the other end
 * of a case's life, and a second modal idiom on one screen would be a second thing to learn.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT EXISTS — THE TECHNICAL DELEGATE'S OWN WORDS, 22 SEPTEMBER 2026
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * *"I also need the ability to pick a previous hearing to edit the details (for example, a
 * change of time and venue for the hearing). Currently I cannot do that and must manually
 * update the letter instead, but the record remains the old one."*
 *
 * Both halves of that sentence matter. The hearing had moved, so he retyped the letter — and
 * the case record went on saying the old time, which is what the register's outcome column,
 * §8's stepper caption, §15's hearing clocks and the Command Desk's `Hearing Scheduled Today`
 * alert are all computed from. The document said one thing and the system said another, and
 * only the document had a human being's attention.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE FIELDS, AND THE ONES THAT ARE NOT HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The time, the place, and why. `CreateHearingModal` captures eleven other things and none of
 * them is offered:
 *
 *  · the **match, the persons involved, the charges and the incident details** are the record
 *    of the charge. Ruling §K says of the persons that a team list re-loaded a week later must
 *    not rewrite a letter served on a player, and the article a person is charged under is the
 *    same kind of fact. Amending a charge changes what the person has to answer — §2's part 2
 *    makes that a fresh notice, not an amended record — and it would move the case's level and
 *    its minimum penalty with it. It is a heavier act and it is deliberately not built here.
 *  · the **two instants** — when the incident happened and when the report arrived — are what
 *    §15's 24-hour rule is measured from. They are history, not arrangements.
 *  · the **Team Manager** is editable already, on the case head and on batch 1's card
 *    (`TeamManagerField`). A second control for one column is how a column ends up with two
 *    rules.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FORM CANNOT REACH THE TWO REFUSALS IT WOULD OTHERWISE MEET
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The server refuses an amendment that moves nothing, and one with no reason. Rather than keep
 * a second copy of either sentence — see `amendmentText.ts` — `Save Amendment` is disabled
 * until both conditions hold, with a hint under it saying which is outstanding. A real disabled
 * button plus a hint is `ServiceCapture`'s idiom for a control that cannot yet do its job, and
 * it is used here for the same reason: an absent control explains nothing.
 *
 * What a reason may CONTAIN is checked with `@hockeytd/shared`'s `recordTextFault`, which is
 * the route's own rule applied a moment earlier, so the sentence shown is the route's either
 * way (§12a).
 *
 * British English throughout.
 */

import { useEffect, useMemo, useRef, useState } from "react";

import { recordTextFault, recordTextRefusal } from "@hockeytd/shared";

import type { HearingAmendmentRequest, HearingCaseDetailView } from "../../api/types.js";
import {
  AMEND_CANCEL,
  AMEND_NOTE,
  AMEND_PLACE_HINT,
  AMEND_REASON_HINT,
  AMEND_REASON_LABEL,
  AMEND_REDRAFT_CHOICE,
  AMEND_REDRAFT_WARNING,
  AMEND_SAVE,
  AMEND_TIME_HINT,
  AMEND_TITLE,
  AMEND_WHAT,
  amendBlocker,
  amendLetterForecast,
} from "./amendmentText.js";
import { combineDateAndTime, DateTimeField, splitInstant } from "./DateTimeField.js";

/** Every field this form can refuse, so a message and a focus target share one spelling. */
export type AmendField = "hearingAt" | "reason";

export interface AmendHearingModalProps {
  /** The case being amended. The form is pre-filled from it and nothing else. */
  hearing: HearingCaseDetailView;
  onCancel: () => void;
  onSave: (input: HearingAmendmentRequest) => void;
  /** True while the POST is in flight. */
  busy: boolean;
  /** A refusal from the API, shown in the foot. Its message is printed verbatim. */
  error: Error | null;
}

/** The place, as the server will normalise it — so "unchanged" means the same thing both sides. */
function normalisePlace(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export function AmendHearingModal({
  hearing,
  onCancel,
  onSave,
  busy,
  error,
}: AmendHearingModalProps): React.JSX.Element {
  /**
   * PRE-FILLED FROM THE RECORD, AND THAT IS WHAT MAKES THE ROUTE'S CONTRACT SAFE.
   *
   * Both scheduling fields are always sent. A field the Technical Delegate does not touch
   * therefore arrives holding what the record already says, so there is no way for this form to
   * amend the time while quietly clearing the place — which a "send only what changed" body
   * would make possible the first time somebody forgot a field.
   */
  const initial = useMemo(() => splitInstant(hearing.hearingAt), [hearing.hearingAt]);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [place, setPlace] = useState(hearing.hearingLocation ?? "");
  const [reason, setReason] = useState("");
  const [redraftLetter, setRedraftLetter] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<AmendField, string>>>({});

  const dateRef = useRef<HTMLInputElement | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);

  // Escape closes. Bound on the document so it works wherever focus is — the create modal's
  // own effect, and for its reason.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
    };
  }, [onCancel]);

  const letterState = hearing.documents[1];
  const letterServedAt =
    hearing.documentDetail.find((document) => document.batch === 1)?.servedAt ?? null;
  const forecast = amendLetterForecast(letterState, letterServedAt);

  const instant = combineDateAndTime(date, time);
  const nextAt = instant.kind === "instant" ? instant.iso : null;
  const nextPlace = normalisePlace(place);

  /**
   * HAS ANYTHING MOVED — compared the way the server compares it.
   *
   * The instants are compared as INSTANTS and not as strings: `2026-09-23T14:30:00+09:00` and
   * `2026-09-23T05:30:00Z` are the same moment, and a string comparison would offer to save an
   * amendment that moved nothing the moment the record came back in a different spelling. The
   * place is trimmed first, so retyping the same venue with a trailing space is not an
   * amendment either.
   */
  const moved =
    (() => {
      const before = hearing.hearingAt === null ? null : new Date(hearing.hearingAt).getTime();
      const after = nextAt === null ? null : new Date(nextAt).getTime();
      return before !== after;
    })() || (hearing.hearingLocation ?? null) !== nextPlace;

  const reasonHasText = recordTextFault(reason)?.kind !== "no_visible_text" && reason !== "";
  // A half-typed instant is not a saveable amendment whatever else is true of the form, and it
  // is the one thing here the server would answer with a 400 rather than a sentence.
  const halfInstant = instant.kind === "partial" || instant.kind === "invalid";
  const blocker = halfInstant ? null : amendBlocker({ moved, hasReason: reasonHasText });

  function clearField(field: AmendField): void {
    setFieldErrors((current) => {
      if (current[field] === undefined) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  function save(): void {
    const errors: Partial<Record<AmendField, string>> = {};

    if (instant.kind === "partial") {
      errors.hearingAt =
        instant.missing === "date"
          ? "Give the date as well as the time, or clear both to postpone the hearing."
          : "Give the time as well as the date, or clear both to postpone the hearing.";
    } else if (instant.kind === "invalid") {
      errors.hearingAt = "That is not a date this calendar has.";
    }

    // §12a's rule, from the shared module the route uses. A reason of nothing but spaces or
    // invisible characters is not refused here — it simply leaves `Save` disabled, and the
    // hint under it says a reason is needed.
    const fault = recordTextFault(reason);
    if (fault !== null && fault.kind !== "no_visible_text") {
      errors.reason = recordTextRefusal("The reason", fault);
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      if (errors.hearingAt !== undefined) dateRef.current?.focus();
      else reasonRef.current?.focus();
      return;
    }

    setFieldErrors({});
    onSave({
      hearingAt: nextAt,
      hearingLocation: nextPlace,
      reason: reason.trim(),
      // Sent only when it is true, and only when it could be honoured. The route refuses a
      // re-draft of a letter that is not a draft, by name, rather than ignoring it — so the
      // form does not send one it knows would be refused.
      ...(letterState === "draft" && redraftLetter ? { redraftLetter: true } : {}),
    });
  }

  return (
    <div
      className="hmodal"
      data-testid="amendBackdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        className="hmodal__box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="amendTitle"
        data-testid="amendModal"
      >
        <div className="panel__head">
          <h3 className="panel__title" id="amendTitle">
            {AMEND_TITLE}
          </h3>
          <span className="panel__note">{AMEND_NOTE}</span>
          <span className="spacer" />
          <button type="button" className="gbtn gbtn--sm" onClick={onCancel}>
            Close
          </button>
        </div>

        <div className="hmodal__body">
          {/* WHICH CASE. A modal that amends a disciplinary hearing says whose hearing it is
              amending, at the top, before a field is offered — the case file behind it is
              covered by the backdrop. */}
          <p className="field__sub" data-testid="amendWhichCase">
            <b>{hearing.caseNumber}</b>
            {hearing.teamCode === null ? "" : ` · ${hearing.teamCode}`} ·{" "}
            {hearing.personsInvolved.map((person) => person.name).join(", ") ||
              "no person named"}
          </p>
          <p className="field__sub" data-testid="amendWhat">
            {AMEND_WHAT}
          </p>

          <div className="formgrid">
            <DateTimeField
              label="Time Of The Hearing"
              hint={AMEND_TIME_HINT}
              date={date}
              time={time}
              dateRef={dateRef}
              invalid={fieldErrors.hearingAt !== undefined}
              message={fieldErrors.hearingAt}
              onChangeDate={(value) => {
                setDate(value);
                clearField("hearingAt");
              }}
              onChangeTime={(value) => {
                setTime(value);
                clearField("hearingAt");
              }}
            />
            <label className="field">
              <span className="field__label">Place Of The Hearing</span>
              <span className="field__sub">{AMEND_PLACE_HINT}</span>
              <input
                aria-label="Place Of The Hearing"
                value={place}
                autoComplete="off"
                onChange={(event) => {
                  setPlace(event.target.value);
                }}
              />
            </label>
          </div>

          <div
            className={`field${fieldErrors.reason === undefined ? "" : " field--bad"}`}
            style={{ marginTop: 12 }}
          >
            <label className="field__label" htmlFor="amendReason">
              {AMEND_REASON_LABEL}
            </label>
            <span className="field__sub">{AMEND_REASON_HINT}</span>
            <textarea
              id="amendReason"
              ref={reasonRef}
              rows={2}
              value={reason}
              aria-invalid={fieldErrors.reason === undefined ? undefined : true}
              onChange={(event) => {
                setReason(event.target.value);
                clearField("reason");
              }}
            />
            {fieldErrors.reason === undefined ? null : (
              <span className="field__bad" role="alert" data-testid="amendReasonBad">
                {fieldErrors.reason}
              </span>
            )}
          </div>

          {/* WHAT THIS WILL DO TO THE LETTER, before Save is pressed. The letter is why the
              feature was asked for, so its state is not something to discover afterwards. */}
          <div
            className={`warnnote warnnote--${forecast.kind}`}
            role="note"
            data-testid="amendLetterForecast"
          >
            <span className="warnnote__kind">Hearing Letter</span>
            <span className="warnnote__msg">{forecast.text}</span>
          </div>

          {letterState === "draft" ? (
            <div className="field" data-testid="amendRedraftChoice">
              <span className="field__label">The Draft Letter</span>
              <span className="field__sub">{AMEND_REDRAFT_WARNING}</span>
              <div className="tagpal__chips">
                {AMEND_REDRAFT_CHOICE.map((choice) => (
                  <button
                    key={String(choice.value)}
                    type="button"
                    className={`tag${redraftLetter === choice.value ? " tag--on" : ""}`}
                    aria-pressed={redraftLetter === choice.value}
                    onClick={() => {
                      setRedraftLetter(choice.value);
                    }}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="panel__foot hmodal__acts">
          {error === null ? null : (
            <p className="rowerror" role="alert" data-testid="amendApiError">
              {/* The server's own sentence. §14b's refusals name the remedy — reopen the
                  minutes — and a message composed here would not. */}
              {error.message}
            </p>
          )}
          {blocker === null ? null : (
            <span className="btnhint" data-testid="amendBlocker">
              {blocker}
            </span>
          )}
          <span className="spacer" />
          <button type="button" className="gbtn" onClick={onCancel}>
            {AMEND_CANCEL}
          </button>
          <button
            type="button"
            className="gbtn gbtn--solid"
            disabled={busy || blocker !== null}
            onClick={save}
            data-testid="amendSave"
          >
            {busy ? "Working…" : AMEND_SAVE}
          </button>
        </div>
      </div>
    </div>
  );
}
